/* eslint-disable react/prop-types */
import React from 'react';
import { Modal, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Form } from 'react-bootstrap-5';
import { useDropzone } from 'react-dropzone';
import { createConsumer } from '@rails/actioncable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import Button from 'shared/button';
import BrokersList from './brokers_list';

import 'animate.css';
import { pick } from 'lodash';

const ACCEPTED_FILE_TYPES = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx'
};

const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);
const ACCEPTED_EXTENSIONS = Object.values(ACCEPTED_FILE_TYPES).join(', ');

type Broker = {
  value: number | string;
  label: string
 } | null;

type NewPropertyFromUploadProps = {
  onSave: (params: { brokerIds: (number | string)[]; propertyParams?: any; researchPropertyParams?: any; aiPopulatedFields?: string[] }) => void;
  gotoPrevStep?: () => void;
  maxBrokersLimit: number;
  showBrokerSelect: boolean;
};

enum ModalSteps {
  ACCEPTING_UPLOAD = 'accepting_upload',
  UPLOADING = 'uploading',
  PROCESSING_UPLOAD = 'processing_upload',
  UPLOAD_ERROR = 'upload_error',
  PROCESSING_ERROR = 'processing_error',
  PROCESSING_SUCCESS = 'processing_success'
}

type UploadResponse = {
  upload_id: number;
}
type ProcessedResponse = {
  llmData: { [key: string]: string },
  propertyFormData: { [key: string]: string },
  populatedFieldNames: string[]
};
type ProcessingErrorResponse = {
  error: { code: string; message: string }
} | null;
type UploadErrorResponse = Error | null; // holds a JS error

const NewPropertyFromUpload: React.FC<NewPropertyFromUploadProps> = ({
  onSave,
  gotoPrevStep,
  maxBrokersLimit,
  showBrokerSelect
}) => {
  const [modalStatus, setModalStatus] = React.useState<ModalSteps>(ModalSteps.ACCEPTING_UPLOAD);
  const [processedResponse, setProcessedResponse] = React.useState<ProcessedResponse | null>(null);
  const [uploadErrorResponse, setUploadErrorResponse] = React.useState<UploadErrorResponse>(null);
  const [processingErrorResponse, setProcessingErrorResponse] = React.useState<ProcessingErrorResponse>(null);
  const [uploadUUID, setUploadUUID] = React.useState<string | null>(null); // used to uniquely identify this upload session
  const consumerRef = React.useRef<any>(null);
  const subscriptionRef = React.useRef<any>(null);

  // We add an empty broker to make the dropdown appear w/o needing to manually add one
  // TODO: change BrokersList so `required=true` automatically adds one empty broker
  const [selectedBrokers, setSelectedBrokers] = React.useState<Broker[]>([null]);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [fileToUpload, setFileToUpload] = React.useState<File | null>(null);
  const [fileDropError, setFileDropError] = React.useState<string>('');

  // assumes array is never empty
  const isBrokersInvalid = showBrokerSelect && selectedBrokers.some(item => item == null);

  const uploadDisabled = modalStatus === ModalSteps.UPLOADING || modalStatus === ModalSteps.PROCESSING_UPLOAD;
  const progressText: string = (() => {
    switch (modalStatus) {
      case ModalSteps.UPLOADING:
        return 'Uploading...';
      case ModalSteps.PROCESSING_UPLOAD:
        return 'Processing...';
      default:
        return '';
    }
  })();

  const onUploadDocSelected = (file: File) => {
    const newUUID = uuid();
    setUploadUUID(newUUID);
    setFileToUpload(file);
    setModalStatus(ModalSteps.UPLOADING);
  };

  React.useEffect(() => {
    if (!uploadUUID || !fileToUpload) return;

    if (!consumerRef.current) {
      consumerRef.current = createConsumer();
    }
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'Projects::ListingDocUploadChannel',
        upload_uuid: uploadUUID,
      },
      {
        connected: async () => {
          // Only upload after socket is connected to avoid missing messages
          try {
            const formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('upload_uuid', uploadUUID);

            const response = await axios.post<UploadResponse>('property_analyze_doc/upload',
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setModalStatus(ModalSteps.PROCESSING_UPLOAD);
          } catch (err) {
            setUploadErrorResponse(err);
            setModalStatus(ModalSteps.UPLOAD_ERROR);
          }
        },
        disconnected: () => {},
        // TODO: we probably want the backend to send a periodic message to say it's still working, and front-end should
        // time out if no message is received
        received: (payload: any) => {
          if (payload.status === 'error') {
            setProcessingErrorResponse(payload.error);
            setModalStatus(ModalSteps.PROCESSING_ERROR);
          } else {
            setProcessedResponse(payload.data as ProcessedResponse);
            setModalStatus(ModalSteps.PROCESSING_SUCCESS);
          }

          // also forces an unsubscribe & disconnect
          setUploadUUID(null);
          setFileToUpload(null);
        }
      }
    );

    // Cleanup
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect();
        consumerRef.current = null;
      }
    };
  }, [uploadUUID, fileToUpload]);

  const canSave = modalStatus === ModalSteps.PROCESSING_SUCCESS && !isBrokersInvalid && !saving;

  const onDrop = (acceptedFiles: File[]) => {
    setFileDropError('');
    if (acceptedFiles) {
      if (acceptedFiles.length === 1) {
        onUploadDocSelected(acceptedFiles[0]);
      } else {
        setFileDropError('Only 1 file can be uploaded');
        return;
      }
    }
  };

  const onDropRejected = (rejectedFiles: any[]) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      setFileDropError(`Invalid file type. Please upload a ${ACCEPTED_EXTENSIONS} file.`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: uploadDisabled || isBrokersInvalid,
    accept: ACCEPTED_MIME_TYPES
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canSave) {
      setSaving(true);
      const formFields = processedResponse?.propertyFormData || {};

      const researchPropertyParams =
        pick(formFields, ['address', 'city', 'county', 'state', 'zip', 'country_id', 'property_type_id',
                          'building_size', 'building_class']); // eslint-disable-line indent
      const propertyParams = pick(formFields, ['lease_checked', 'sale_checked', 'name', 'location_description']);
      const aiPopulatedFields = processedResponse?.populatedFieldNames || [];
      onSave({
        brokerIds: selectedBrokers.filter(broker => broker != null).map(broker => broker?.value),
        propertyParams,
        researchPropertyParams,
        aiPopulatedFields
      });
    }
  };

  function renderDocAnalysisPanel() {
    function renderUploadError() {
      // TODO: log to airbrake (unify logic from media_tab.js) - but only if backend didn't
      console.error('Upload error response:', uploadErrorResponse); // eslint-disable-line no-console

      return (
        <div className="alert alert-danger my-4 text-center">
          Something went wrong while your document was uploading. Please wait a few minutes and try again.
        </div>
      );
    }

    function renderProcessingError() {
      // TODO: if we were rate-limited, show a more specific message
      return (
        <div className="alert alert-danger my-4 text-center">
          Something went wrong while processing your document. Please wait a few minutes and try again.
        </div>
      );
    }

    function renderProcessedResponseFields() {
      return (
        <div className="my-4">
          {/* TODO: warn the user either here or in PEF to check the fields because AI can make mistakes */}
          <h5 className="mb-3">Extracted Listing Data</h5>
          <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
            <table className="table table-bordered">
              <tbody>
                {processedResponse?.llmData &&
                  Object.entries(processedResponse.llmData).map(([key, value]) => (
                    <tr key={key}>
                      <th style={{ width: '30%' }}>{key}</th>
                      <td>{value == null ? '' : String(value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    function renderUploadDropzone() {
      return (
        <React.Fragment>
          {/* TODO: Add a dialog to display data retention policies, i.e. we'll keep your data for X days; this will
              depend on OpenAI's policies */}
          {!isBrokersInvalid && (
            <div className="alert alert-warning mb-3">
              Please make sure your document does not contain sensitive information such as passwords, credit card
              numbers, or social security numbers.
            </div>
          )}
          <div
            className="btn btn-light d-block py-4 my-4"
            {...getRootProps()}
            style={{
              ...(isDragActive && { border: 'thin dashed #007bff', background: '#f0f8ff' }),
              ...(isBrokersInvalid && { opacity: 0.5, pointerEvents: 'none' })
            }}
          >
            <input
              {...getInputProps({
                multiple: false
              })}
            />
            <div className="text-center text-primary">
              <FontAwesomeIcon icon={faUpload} size="3x" />
            </div>
            <h5 className="text-center" style={{ fontSize: '15px' }}>
              Drag or click to upload a listing document{' '}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="file-types-tooltip">
                    Accepted file types: {ACCEPTED_EXTENSIONS}
                  </Tooltip>
                }
              >
                <span style={{ cursor: 'help' }}>
                  <FontAwesomeIcon icon={faCircleInfo} size="sm" className="text-muted" />
                </span>
              </OverlayTrigger>
            </h5>
            {progressText && (
              <div className="text-center mt-3 text-muted">
                {progressText}
              </div>
            )}
            {isBrokersInvalid && (
              <div className="text-center mt-3 text-danger">
                Please select a broker to enable upload
              </div>
            )}
          </div>
          {fileDropError && (
            <div className="text-center mt-3 text-danger">
              {fileDropError}
            </div>
          )}
        </React.Fragment>
      );
    }

    switch (modalStatus) {
      case ModalSteps.PROCESSING_ERROR:
        return renderProcessingError();
      case ModalSteps.UPLOAD_ERROR:
        return renderUploadError();
      case ModalSteps.PROCESSING_SUCCESS:
        return renderProcessedResponseFields();
      default:
        return renderUploadDropzone();
    }
  }

  return (
    <Modal.Body className="py-5">
      <h2 className="text-center mb-5">
        <strong>Upload a listing document</strong>
      </h2>
      <Row>
        <Col md={8} mdOffset={2}>
          <Form onSubmit={handleSubmit}>
            <div className="mb-4">
              <h5>Select a Broker</h5>
              <BrokersList
                brokers={selectedBrokers}
                maxBrokersLimit={maxBrokersLimit}
                required={true}
                onSetBrokers={setSelectedBrokers}
              />
            </div>

            {renderDocAnalysisPanel()}

            <div className="mt-5">
              {gotoPrevStep && (
                <Button.Accent className="mr-3" outlined type="button" onClick={gotoPrevStep}>Back</Button.Accent>
              )}
              <Button.Primary disabled={!canSave} name="submit-listing" type="submit">
                {saving ? 'Creating Listing...' : 'Create Listing'}
              </Button.Primary>
            </div>
          </Form>
        </Col>
      </Row>
    </Modal.Body>
  );
};

export default NewPropertyFromUpload;
